"use client"

import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

/** Radix Select/Dropdown/Popover render in a portal outside DialogContent. */
function isRadixPortaledOverlay(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false
  return Boolean(
    target.closest("[data-radix-select-content]") ||
      target.closest("[data-radix-select-viewport]") ||
      target.closest("[data-radix-popper-content-wrapper]") ||
      target.closest('[role="listbox"]') ||
      target.closest("[data-radix-dropdown-menu-content]") ||
      target.closest("[data-radix-popover-content]") ||
      target.closest("[data-radix-menu-content]")
  )
}

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-foreground/80 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:pointer-events-none",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & { hideCloseButton?: boolean }
>(({ className, children, hideCloseButton, onPointerDownOutside, onFocusOutside, ...props }, ref) => {
  // Check if this is a fullscreen dialog
  const isFullscreen = className?.includes('w-screen') || className?.includes('max-w-none');

  // ⚠️ Z-LAYERING CONTRACT (regression 2026-07-20): DialogContent sits ABOVE the overlay
  // but BELOW every floating layer (select/dropdown-menu/popover/tooltip/menubar, all
  // z-[60]). Raising the dialog z-index to or past the floating layers makes every
  // dropdown inside a dialog open INVISIBLY behind the panel (VisioMeter's test and
  // camera pickers "stopped working"). If you change any z here, keep floating layers
  // strictly higher — test/ui/components/z-layering-contract.test.ts enforces this.

  const handlePointerDownOutside: React.ComponentProps<
    typeof DialogPrimitive.Content
  >["onPointerDownOutside"] = (event) => {
    if (isRadixPortaledOverlay(event.target)) {
      event.preventDefault()
    }
    onPointerDownOutside?.(event)
  }

  const handleFocusOutside: React.ComponentProps<
    typeof DialogPrimitive.Content
  >["onFocusOutside"] = (event) => {
    if (isRadixPortaledOverlay(event.target)) {
      event.preventDefault()
    }
    onFocusOutside?.(event)
  }

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        onPointerDownOutside={handlePointerDownOutside}
        onFocusOutside={handleFocusOutside}
        // aria-describedby intentionally not forced: emitting it as undefined strips
        // Radix's automatic Content→Description linkage. Callers may still pass one.
        className={isFullscreen ?
          // Fullscreen: fill viewport from the top. z-[51] keeps the panel above the overlay.
          // Do not animate this branch, including opacity-only `animate-in`: the shared
          // enter keyframe still declares an identity transform, which can poison mobile
          // hit-testing for cross-origin iframes even after the animation completes.
          cn(
            "fixed inset-0 z-[51] flex min-h-0 w-screen flex-col gap-4 overflow-y-auto overscroll-contain rounded-none border-0 bg-background glass-overlay p-4 sm:p-6 m-0",
            className
          ) :
          // Centered card. Do NOT use slide-in-from-left/top (or matching slide-out) —
          // those set enter translates that conflict with translate-x/y-[-50%] and can leave
          // tall dialogs mostly above the viewport (only the footer button visible).
          cn(
            "fixed left-[50%] top-[50%] z-[51] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background glass-overlay p-4 sm:p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:pointer-events-none sm:rounded-lg max-h-[90vh] overflow-y-auto overscroll-contain",
            className
          )
        }
        {...props}
      >
        {children}
        {!hideCloseButton && (
          <DialogPrimitive.Close className={`${isFullscreen ? 'fixed right-4 top-4' : 'absolute right-3 top-3 sm:right-4 sm:top-4'} pointer-events-auto rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground h-10 w-10 flex items-center justify-center touch-target-min z-50`}>
            <X className="h-5 w-5" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight text-foreground",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
